import {useEffect, useMemo} from "react";
import {Controller, useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Alert, Space} from "antd";
import {Button, Loading} from "~/components";
import {InputField, SelectField} from "~/components/Forms";
import {describeCron, makeCrontime} from "../../utils/cronReview";

const presets = [
    {value: 'every-minute', label: 'Mỗi phút'},
    {value: 'hourly', label: 'Mỗi giờ'},
    {value: 'daily', label: 'Mỗi ngày'},
    {value: 'weekly', label: 'Mỗi tuần'},
    {value: 'monthly', label: 'Mỗi tháng'},
    {value: 'custom', label: 'Tùy chỉnh'},
];

const presetValues = {
    'every-minute': {minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*'},
    hourly: {minute: '0', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*'},
    daily: {minute: '30', hour: '0', dayOfMonth: '*', month: '*', dayOfWeek: '*'},
    weekly: {minute: '30', hour: '0', dayOfMonth: '*', month: '*', dayOfWeek: '1'},
    monthly: {minute: '30', hour: '0', dayOfMonth: '1', month: '*', dayOfWeek: '*'},
};

function CronJobFormAdd({onHandleSubmit})
{
    const initialValues = {
        preset: 'every-minute',
        minute: '*',
        hour: '*',
        dayOfMonth: '*',
        month: '*',
        dayOfWeek: '*',
        url: '',
    };

    const validationSchema = Yup.object().shape({
        minute: Yup.string().required('Minute is required'),
        hour: Yup.string().required('Hour is required'),
        dayOfMonth: Yup.string().required('Day of month is required'),
        month: Yup.string().required('Month is required'),
        dayOfWeek: Yup.string().required('Day of week is required'),
        url: Yup.string().required('Webhook URL is required').url('Webhook URL is invalid'),
    });

    const {control, handleSubmit, formState: {isSubmitting, errors}, setValue} = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema),
    });

    const values = useWatch({control});

    const cronPayload = useMemo(() => ({
        minute: values?.minute || '*',
        hour: values?.hour || '*',
        dayOfMonth: values?.dayOfMonth || '*',
        month: values?.month || '*',
        dayOfWeek: values?.dayOfWeek || '*',
    }), [values?.minute, values?.hour, values?.dayOfMonth, values?.month, values?.dayOfWeek]);

    const review = useMemo(() => ({
        crontime: makeCrontime(cronPayload),
        description: describeCron(cronPayload),
    }), [cronPayload]);

    useEffect(() => {
        const presetValue = presetValues[values?.preset];
        if(!presetValue) return;

        Object.entries(presetValue).forEach(([key, value]) => setValue(key, value));
    }, [values?.preset, setValue]);

    const onSubmit = async (data) => {
        await onHandleSubmit({
            minute: data.minute,
            hour: data.hour,
            dayOfMonth: data.dayOfMonth,
            month: data.month,
            dayOfWeek: data.dayOfWeek,
            url: data.url,
        });
    };

    return (
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
            {isSubmitting && <Loading />}

            <Controller control={control} name="preset" render={({field}) => (
                <SelectField label="Kiểu lịch chạy" options={presets} errors={errors} {...field} />
            )}/>

            {values?.preset === 'custom' && (
                <div className="row">
                    <div className="col-md-4">
                        <Controller control={control} name="minute" render={({field}) => (
                            <InputField label="Minute" errors={errors} {...field} />
                        )}/>
                    </div>
                    <div className="col-md-4">
                        <Controller control={control} name="hour" render={({field}) => (
                            <InputField label="Hour" errors={errors} {...field} />
                        )}/>
                    </div>
                    <div className="col-md-4">
                        <Controller control={control} name="dayOfMonth" render={({field}) => (
                            <InputField label="Day of Month" errors={errors} {...field} />
                        )}/>
                    </div>
                    <div className="col-md-4">
                        <Controller control={control} name="month" render={({field}) => (
                            <InputField label="Month" errors={errors} {...field} />
                        )}/>
                    </div>
                    <div className="col-md-4">
                        <Controller control={control} name="dayOfWeek" render={({field}) => (
                            <InputField label="Day of Week" errors={errors} {...field} />
                        )}/>
                    </div>
                </div>
            )}

            <Controller control={control} name="url" render={({field}) => (
                <InputField label="Webhook URL" errors={errors} {...field} />
            )}/>

            <Alert
                type="info"
                showIcon
                message="Lịch chạy"
                description={`${review.description} (${review.crontime})`}
                style={{marginBottom: 16}}
            />

            <div className="form-group d-flex justify-content-end modal-bottom">
                <Space>
                    <Button primary type="submit">Thêm cronjob</Button>
                </Space>
            </div>
        </form>
    );
}

export default CronJobFormAdd;
